import React, { useState, useRef, useEffect } from 'react';
import axios from '../config/axiosConfig';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    List,
    ListItem,
    ListItemText,
    Popper,
    ClickAwayListener,
    Grow,
    Stack,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Grid,
    Container,
    Collapse,
    Slider,
    FormControlLabel,
    Switch,
    Divider,
    Menu,
    MenuItem,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import { exportToPDF, exportToImage, exportDividendCSV } from '../utils/dividendExportUtils';

const DividendCalculator = () => {
    const theme = useTheme();

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const inputRef = useRef(null);
    const debounceTimeout = useRef(null);

    // Holdings state
    const [holdings, setHoldings] = useState(() => {
        // Initialize from localStorage if available
        try {
            const savedHoldings = localStorage.getItem('dividendCalculatorHoldings');
            return savedHoldings ? JSON.parse(savedHoldings) : [];
        } catch (error) {
            console.error('Error loading saved holdings:', error);
            return [];
        }
    });
    const [dividendData, setDividendData] = useState({});
    const [loadingDividends, setLoadingDividends] = useState(false);

    // Save holdings to localStorage whenever they change
    useEffect(() => {
        try {
            localStorage.setItem('dividendCalculatorHoldings', JSON.stringify(holdings));
        } catch (error) {
            console.error('Error saving holdings:', error);
        }
    }, [holdings]);

    // Projection state
    const [showProjections, setShowProjections] = useState(false);
    const [enableDRIP, setEnableDRIP] = useState(false);
    const [yearsHorizon, setYearsHorizon] = useState(10);
    const [dividendGrowthRate, setDividendGrowthRate] = useState(3);
    const [sharePriceGrowthRate, setSharePriceGrowthRate] = useState(5);
    const [showYearlyBreakdown, setShowYearlyBreakdown] = useState(false);

    // Export menu state (for mobile)
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Export handlers
    const handleExportPDF = () => {
        setExportMenuAnchor(null);
        const settings = {
            yearsHorizon,
            enableDRIP,
            dividendGrowthRate,
            sharePriceGrowthRate
        };
        exportToPDF(holdings, dividendData, totals, projections, settings);
    };

    const handleExportImage = async () => {
        setExportMenuAnchor(null);
        await exportToImage('dividend-calculator-container');
    };

    const handleExportCSV = () => {
        setExportMenuAnchor(null);
        const settings = showProjections ? {
            yearsHorizon,
            enableDRIP,
            dividendGrowthRate,
            sharePriceGrowthRate
        } : null;
        exportDividendCSV(holdings, dividendData, showProjections ? projections : null, settings);
    };

    // Search for tradable assets
    const searchTradableAssets = async (query) => {
        if (!query || query.length < 1) {
            setSearchResults([]);
            return;
        }

        setSearchLoading(true);
        try {
            const response = await axios.get('/api/search-tradable', {
                params: { query }
            });
            setSearchResults(response.data);
        } catch (error) {
            console.error('Error searching assets:', error);
        } finally {
            setSearchLoading(false);
        }
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const { value } = e.target;
        const uppercaseValue = value.toUpperCase();
        setSearchQuery(uppercaseValue);
        setAnchorEl(e.currentTarget);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            searchTradableAssets(uppercaseValue);
        }, 300);
    };

    // Handle asset selection from search
    const handleAssetSelect = (asset) => {
        // Check if already added
        if (holdings.some(h => h.symbol === asset.symbol)) {
            setSearchResults([]);
            setAnchorEl(null);
            setSearchQuery('');
            return;
        }

        // Add to holdings with default 100 shares
        const newHolding = {
            symbol: asset.symbol,
            name: asset.name,
            exchange: asset.exchange,
            price: asset.price || 0,
            shares: 100
        };

        setHoldings(prev => [...prev, newHolding]);
        setSearchResults([]);
        setAnchorEl(null);
        setSearchQuery('');
    };

    // Handle click away from search
    const handleClickAway = () => {
        setSearchResults([]);
        setAnchorEl(null);
    };

    // Update shares for a holding
    const handleSharesChange = (symbol, value) => {
        const shares = value === '' ? 0 : parseFloat(value);
        setHoldings(prev =>
            prev.map(h => h.symbol === symbol ? { ...h, shares } : h)
        );
    };

    // Remove a holding
    const handleRemoveHolding = (symbol) => {
        setHoldings(prev => prev.filter(h => h.symbol !== symbol));
    };

    // Fetch dividend data when holdings change
    useEffect(() => {
        const fetchDividendData = async () => {
            if (holdings.length === 0) {
                setDividendData({});
                return;
            }

            setLoadingDividends(true);
            try {
                const symbols = holdings.map(h => h.symbol).join(',');
                const response = await axios.get('/api/portfolio/dividends-summary', {
                    params: { symbols }
                });

                // Convert array to object keyed by symbol
                const dataMap = {};
                if (response.data.success && response.data.data) {
                    response.data.data.forEach(d => {
                        dataMap[d.symbol] = d.dividend;
                    });
                }
                setDividendData(dataMap);
            } catch (error) {
                console.error('Error fetching dividend data:', error);
            } finally {
                setLoadingDividends(false);
            }
        };

        fetchDividendData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [holdings.map(h => h.symbol).join(',')]);

    // Auto-populate dividend growth rate from API data
    useEffect(() => {
        if (holdings.length > 0 && Object.keys(dividendData).length > 0) {
            // Calculate weighted average growth rate
            let totalValue = 0;
            let weightedGrowth = 0;

            holdings.forEach(h => {
                const dividend = dividendData[h.symbol];
                const holdingValue = (h.price || 0) * h.shares;
                
                if (dividend?.growth !== null && dividend?.growth !== undefined && holdingValue > 0) {
                    totalValue += holdingValue;
                    weightedGrowth += dividend.growth * holdingValue;
                }
            });

            if (totalValue > 0) {
                const avgGrowth = weightedGrowth / totalValue;
                // Only update if it's a reasonable number
                if (avgGrowth > -50 && avgGrowth < 100) {
                    setDividendGrowthRate(Math.max(0, Math.round(avgGrowth * 10) / 10));
                }
            }
        }
    }, [dividendData, holdings]);

    // Calculate totals
    const calculateTotals = () => {
        let totalAnnualIncome = 0;
        let totalValue = 0;

        holdings.forEach(h => {
            const dividend = dividendData[h.symbol];
            if (dividend?.amount && h.shares) {
                totalAnnualIncome += dividend.amount * h.shares;
            }
            totalValue += (h.price || 0) * h.shares;
        });

        return {
            totalAnnualIncome,
            totalQuarterlyIncome: totalAnnualIncome / 4,
            totalMonthlyIncome: totalAnnualIncome / 12,
            totalValue,
            averageYield: totalValue > 0 ? (totalAnnualIncome / totalValue) * 100 : 0
        };
    };

    // Calculate future projections
    const calculateProjections = () => {
        const totals = calculateTotals();
        const yearlyData = [];
        
        let currentShares = {};
        let totalDividendsEarned = 0;
        
        // Separate dividend-paying stocks from non-dividend stocks
        const dividendStocks = [];
        const nonDividendStocks = [];
        
        holdings.forEach(h => {
            const dividend = dividendData[h.symbol];
            currentShares[h.symbol] = h.shares;
            
            if (dividend?.amount && dividend.amount > 0) {
                dividendStocks.push(h);
            } else {
                nonDividendStocks.push(h);
            }
        });

        // // Calculate initial values
        // let initialDividendValue = 0;
        // let initialNonDividendValue = 0;
        
        // dividendStocks.forEach(h => {
        //     initialDividendValue += (h.price || 0) * h.shares;
        // });
        
        // nonDividendStocks.forEach(h => {
        //     initialNonDividendValue += (h.price || 0) * h.shares;
        // });

        for (let year = 1; year <= yearsHorizon; year++) {
            let yearlyDividendIncome = 0;
            let dividendsReinvested = 0;
            let dividendPortfolioValue = 0;
            let nonDividendPortfolioValue = 0;

            // Process dividend-paying stocks
            dividendStocks.forEach(h => {
                const dividend = dividendData[h.symbol];
                const shares = currentShares[h.symbol];
                const currentPrice = h.price || 0;
                
                // Apply price growth
                const newPrice = currentPrice * Math.pow(1 + sharePriceGrowthRate / 100, year);
                
                // Apply dividend growth
                const newDividend = dividend.amount * Math.pow(1 + dividendGrowthRate / 100, year);
                
                // Calculate dividend income for this stock
                const dividendIncome = newDividend * shares;
                yearlyDividendIncome += dividendIncome;

                // If DRIP enabled, calculate new shares purchased
                if (enableDRIP && newPrice > 0) {
                    const newSharesPurchased = dividendIncome / newPrice;
                    currentShares[h.symbol] += newSharesPurchased;
                    dividendsReinvested += dividendIncome;
                }

                // Calculate new value for dividend stocks
                dividendPortfolioValue += currentShares[h.symbol] * newPrice;
            });

            // Process non-dividend stocks (just apply price growth)
            nonDividendStocks.forEach(h => {
                const currentPrice = h.price || 0;
                const newPrice = currentPrice * Math.pow(1 + sharePriceGrowthRate / 100, year);
                nonDividendPortfolioValue += h.shares * newPrice;
            });

            const totalPortfolioValue = dividendPortfolioValue + nonDividendPortfolioValue;
            totalDividendsEarned += yearlyDividendIncome;

            yearlyData.push({
                year,
                portfolioValue: totalPortfolioValue,
                annualDividendIncome: enableDRIP ? 0 : yearlyDividendIncome,
                dividendsReinvested: enableDRIP ? dividendsReinvested : 0,
                totalDividendsEarned: enableDRIP ? totalDividendsEarned : yearlyDividendIncome * year,
                yieldOnCost: totals.totalValue > 0 ? (yearlyDividendIncome / totals.totalValue) * 100 : 0
            });
        }

        const finalYear = yearlyData[yearlyData.length - 1];
        
        return {
            yearlyData,
            finalPortfolioValue: finalYear?.portfolioValue || totals.totalValue,
            finalAnnualIncome: finalYear?.annualDividendIncome || totals.totalAnnualIncome,
            totalDividendsEarned: finalYear?.totalDividendsEarned || 0,
            totalGrowth: totals.totalValue > 0 ? 
                ((finalYear?.portfolioValue - totals.totalValue) / totals.totalValue) * 100 : 0,
            finalYieldOnCost: finalYear?.yieldOnCost || totals.averageYield
        };
    };

    const totals = calculateTotals();
    const projections = holdings.length > 0 ? calculateProjections() : null;
    const open = Boolean(anchorEl) && (searchResults.length > 0 || searchLoading);

    // Format currency
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }} id="dividend-calculator-container">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                        Dividend Calculator
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        Add stocks to calculate your expected dividend income
                    </Typography>
                </Box>
                
                {/* Export Buttons - Responsive */}
                {holdings.length > 0 && (
                    <>
                        {isMobile ? (
                            // Mobile: Icon-only dropdown button
                            <>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                                    sx={{ 
                                        border: '1px solid',
                                        borderColor: 'primary.main',
                                        borderRadius: 1,
                                        padding: '2px'
                                    }}
                                >
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                                <Menu
                                    anchorEl={exportMenuAnchor}
                                    open={Boolean(exportMenuAnchor)}
                                    onClose={() => setExportMenuAnchor(null)}
                                >
                                    <MenuItem onClick={handleExportPDF}>
                                        <PictureAsPdfIcon sx={{ mr: 1, fontSize: 18 }} />
                                        PDF
                                    </MenuItem>
                                    <MenuItem onClick={handleExportCSV}>
                                        <TableChartIcon sx={{ mr: 1, fontSize: 18 }} />
                                        CSV
                                    </MenuItem>
                                </Menu>
                            </>
                        ) : (
                            // Desktop: Individual buttons
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={handleExportPDF}
                                    sx={{ textTransform: 'none' }}
                                >
                                    PDF
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ImageIcon />}
                                    onClick={handleExportImage}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Image
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<TableChartIcon />}
                                    onClick={handleExportCSV}
                                    sx={{ textTransform: 'none' }}
                                >
                                    CSV
                                </Button>
                            </Stack>
                        )}
                    </>
                )}
            </Box>

            {/* Search Box */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: theme.palette.background.paper }}>
                <Typography variant="h6" gutterBottom>
                    Add Stocks
                </Typography>

                <ClickAwayListener onClickAway={handleClickAway}>
                    <Box sx={{ position: 'relative', maxWidth: 400 }}>
                        <TextField
                            placeholder="Search by symbol or name..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            fullWidth
                            size="small"
                            inputRef={inputRef}
                            autoComplete="off"
                            InputProps={{
                                startAdornment: <AddIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                        <Popper
                            open={open}
                            anchorEl={anchorEl}
                            placement="bottom-start"
                            transition
                            disablePortal={false}
                            sx={{
                                width: anchorEl ? anchorEl.offsetWidth : 'auto',
                                zIndex: 1500
                            }}
                        >
                            {({ TransitionProps }) => (
                                <Grow {...TransitionProps}>
                                    <Paper
                                        elevation={3}
                                        sx={{
                                            maxHeight: '250px',
                                            overflowY: 'auto',
                                            mt: 1
                                        }}
                                    >
                                        {searchLoading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                                <CircularProgress size={24} />
                                            </Box>
                                        ) : (
                                            <List dense>
                                                {searchResults.map((result, index) => (
                                                    <ListItem
                                                        button
                                                        key={`${result.symbol}-${index}`}
                                                        onClick={() => handleAssetSelect(result)}
                                                        divider={index < searchResults.length - 1}
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: 'action.hover',
                                                            },
                                                            opacity: holdings.some(h => h.symbol === result.symbol) ? 0.5 : 1
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Typography variant="body1" fontWeight="medium">
                                                                        {result.symbol}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        sx={{ fontSize: '0.75em' }}
                                                                    >
                                                                        ({result.exchange})
                                                                    </Typography>
                                                                </Stack>
                                                            }
                                                            secondary={result.name}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}
                                    </Paper>
                                </Grow>
                            )}
                        </Popper>
                    </Box>
                </ClickAwayListener>
            </Paper>

            {/* Summary Cards */}
            {holdings.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <Paper sx={{
                            p: 2,
                            textAlign: 'center',
                            bgcolor: theme.palette.background.paper,
                            minHeight: 100,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <Typography variant="caption" color="textSecondary">
                                Annual Income
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                                {formatCurrency(totals.totalAnnualIncome)}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <Paper sx={{
                            p: 2,
                            textAlign: 'center',
                            bgcolor: theme.palette.background.paper,
                            minHeight: 100,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <Typography variant="caption" color="textSecondary">
                                Quarterly Income
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                                {formatCurrency(totals.totalQuarterlyIncome)}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <Paper sx={{
                            p: 2,
                            textAlign: 'center',
                            bgcolor: theme.palette.background.paper,
                            minHeight: 100,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <Typography variant="caption" color="textSecondary">
                                Monthly Income
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                                {formatCurrency(totals.totalMonthlyIncome)}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <Paper sx={{
                            p: 2,
                            textAlign: 'center',
                            bgcolor: theme.palette.background.paper,
                            minHeight: 100,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <Typography variant="caption" color="textSecondary">
                                Portfolio Value
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                                {formatCurrency(totals.totalValue)}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <Paper sx={{
                            p: 2,
                            textAlign: 'center',
                            bgcolor: theme.palette.background.paper,
                            minHeight: 100,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <Typography variant="caption" color="textSecondary">
                                Avg Yield
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                                {totals.averageYield.toFixed(2)}%
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Holdings Table */}
            {holdings.length > 0 ? (
                <Paper sx={{ bgcolor: theme.palette.background.paper }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Symbol</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell align="right">Shares</TableCell>
                                    <TableCell align="right">Price</TableCell>
                                    <TableCell align="right">Yield</TableCell>
                                    <TableCell align="right">Annual Div</TableCell>
                                    <TableCell align="right">Frequency</TableCell>
                                    <TableCell align="right">Annual Income</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {holdings.map((holding) => {
                                    const dividend = dividendData[holding.symbol];
                                    const annualIncome = dividend?.amount ? dividend.amount * holding.shares : 0;

                                    return (
                                        <TableRow key={holding.symbol}>
                                            <TableCell>
                                                <Typography fontWeight="medium">
                                                    {holding.symbol}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                                                    {holding.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TextField
                                                    type="number"
                                                    value={holding.shares}
                                                    onChange={(e) => handleSharesChange(holding.symbol, e.target.value)}
                                                    size="small"
                                                    sx={{ width: 100 }}
                                                    inputProps={{ min: 0, step: 1 }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {holding.price ? formatCurrency(holding.price) : '--'}
                                            </TableCell>
                                            <TableCell align="right">
                                                {dividend?.yield ? `${dividend.yield.toFixed(2)}%` : '--'}
                                            </TableCell>
                                            <TableCell align="right">
                                                {dividend?.amount ? formatCurrency(dividend.amount) : '--'}
                                            </TableCell>
                                            <TableCell align="right">
                                                {dividend?.frequency || '--'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    fontWeight="medium"
                                                    color={annualIncome > 0 ? 'success.main' : 'text.secondary'}
                                                >
                                                    {annualIncome > 0 ? formatCurrency(annualIncome) : '--'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveHolding(holding.symbol)}
                                                    color="error"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {loadingDividends && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                                Loading dividend data...
                            </Typography>
                        </Box>
                    )}
                </Paper>
            ) : (
                <Paper sx={{
                    p: 4,
                    textAlign: 'center',
                    bgcolor: theme.palette.background.paper
                }}>
                    <Typography color="textSecondary">
                        No stocks added yet. Search and add stocks above to calculate dividend income.
                    </Typography>
                </Paper>
            )}

            {/* Dividend Paying Info */}
            {holdings.length > 0 && (
                <Box sx={{ mt: 2, mb: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                        {Object.keys(dividendData).filter(s => dividendData[s]?.amount > 0).length} of {holdings.length} stocks pay dividends
                    </Typography>
                </Box>
            )}

            {/* Future Projections Section */}
            {holdings.length > 0 && (
                <Paper sx={{ p: 3, bgcolor: theme.palette.background.paper }}>
                    <Button
                        fullWidth
                        onClick={() => setShowProjections(!showProjections)}
                        endIcon={showProjections ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        startIcon={<TrendingUpIcon />}
                        sx={{ justifyContent: 'space-between', mb: showProjections ? 2 : 0 }}
                    >
                        <Typography variant="h6">
                            Future Projections
                        </Typography>
                    </Button>

                    <Collapse in={showProjections}>
                        <Divider sx={{ mb: 3 }} />

                        {/* Projection Settings */}
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={enableDRIP}
                                            onChange={(e) => setEnableDRIP(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body1">
                                                Enable DRIP (Dividend Reinvestment)
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Automatically reinvest dividends to buy more shares
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography gutterBottom>
                                    Time Horizon: {yearsHorizon} years
                                </Typography>
                                <Slider
                                    value={yearsHorizon}
                                    onChange={(e, value) => setYearsHorizon(value)}
                                    min={1}
                                    max={30}
                                    marks={[
                                        { value: 1, label: '1' },
                                        { value: 10, label: '10' },
                                        { value: 20, label: '20' },
                                        { value: 30, label: '30' }
                                    ]}
                                    valueLabelDisplay="auto"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Expected Dividend Growth (%/year)"
                                    type="number"
                                    value={dividendGrowthRate}
                                    onChange={(e) => setDividendGrowthRate(parseFloat(e.target.value) || 0)}
                                    fullWidth
                                    size="small"
                                    inputProps={{ step: 0.1, min: -10, max: 50 }}
                                    helperText="Auto-populated from historical data"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Expected Share Price Growth (%/year)"
                                    type="number"
                                    value={sharePriceGrowthRate}
                                    onChange={(e) => setSharePriceGrowthRate(parseFloat(e.target.value) || 0)}
                                    fullWidth
                                    size="small"
                                    inputProps={{ step: 0.1, min: -10, max: 50 }}
                                />
                            </Grid>
                        </Grid>

                        {/* Projection Results */}
                        {projections && (
                            <>
                                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                    Projected Results After {yearsHorizon} Years
                                </Typography>

                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6} md={3}>
                                        <Paper sx={{ 
                                            p: 2, 
                                            textAlign: 'center', 
                                            bgcolor: 'rgba(76, 175, 80, 0.1)',
                                            minHeight: 120,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center'
                                        }}>
                                            <Typography variant="caption" color="textSecondary">
                                                Portfolio Value
                                            </Typography>
                                            <Typography variant="h6" fontWeight="bold" color="success.main">
                                                {formatCurrency(projections.finalPortfolioValue)}
                                            </Typography>
                                            <Typography variant="caption" color="success.main">
                                                +{projections.totalGrowth.toFixed(1)}%
                                            </Typography>
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={6} md={3}>
                                        <Paper sx={{ 
                                            p: 2, 
                                            textAlign: 'center', 
                                            bgcolor: 'rgba(33, 150, 243, 0.1)',
                                            minHeight: 120,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center'
                                        }}>
                                            <Typography variant="caption" color="textSecondary">
                                                {enableDRIP ? 'Total Dividends Earned' : 'Annual Income'}
                                            </Typography>
                                            <Typography variant="h6" fontWeight="bold" color="primary">
                                                {formatCurrency(enableDRIP ? projections.totalDividendsEarned : projections.finalAnnualIncome)}
                                            </Typography>
                                            {!enableDRIP && (
                                                <Typography variant="caption" color="primary">
                                                    {formatCurrency(projections.finalAnnualIncome / 12)}/month
                                                </Typography>
                                            )}
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={6} md={3}>
                                        <Paper sx={{ 
                                            p: 2, 
                                            textAlign: 'center', 
                                            bgcolor: 'rgba(255, 152, 0, 0.1)',
                                            minHeight: 120,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center'
                                        }}>
                                            <Typography variant="caption" color="textSecondary">
                                                Yield on Cost
                                            </Typography>
                                            <Typography variant="h6" fontWeight="bold" color="warning.main">
                                                {projections.finalYieldOnCost.toFixed(2)}%
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                vs {totals.averageYield.toFixed(2)}% today
                                            </Typography>
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={6} md={3}>
                                        <Paper sx={{ 
                                            p: 2, 
                                            textAlign: 'center', 
                                            bgcolor: 'rgba(156, 39, 176, 0.1)',
                                            minHeight: 120,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center'
                                        }}>
                                            <Typography variant="caption" color="textSecondary">
                                                Total Gain
                                            </Typography>
                                            <Typography variant="h6" fontWeight="bold" color="secondary.main">
                                                {formatCurrency(projections.finalPortfolioValue - totals.totalValue)}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                {/* Year-by-Year Breakdown */}
                                <Button
                                    onClick={() => setShowYearlyBreakdown(!showYearlyBreakdown)}
                                    endIcon={showYearlyBreakdown ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    sx={{ mb: 2 }}
                                >
                                    {showYearlyBreakdown ? 'Hide' : 'Show'} Year-by-Year Breakdown
                                </Button>

                                <Collapse in={showYearlyBreakdown}>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Year</TableCell>
                                                    <TableCell align="right">Portfolio Value</TableCell>
                                                    {!enableDRIP && <TableCell align="right">Annual Income</TableCell>}
                                                    {enableDRIP && <TableCell align="right">Dividends Reinvested</TableCell>}
                                                    <TableCell align="right">Total Dividends</TableCell>
                                                    <TableCell align="right">Yield on Cost</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {projections.yearlyData.map((data) => (
                                                    <TableRow key={data.year}>
                                                        <TableCell>{data.year}</TableCell>
                                                        <TableCell align="right">{formatCurrency(data.portfolioValue)}</TableCell>
                                                        {!enableDRIP && <TableCell align="right">{formatCurrency(data.annualDividendIncome)}</TableCell>}
                                                        {enableDRIP && <TableCell align="right">{formatCurrency(data.dividendsReinvested)}</TableCell>}
                                                        <TableCell align="right">{formatCurrency(data.totalDividendsEarned)}</TableCell>
                                                        <TableCell align="right">{data.yieldOnCost.toFixed(2)}%</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Collapse>
                            </>
                        )}
                    </Collapse>
                </Paper>
            )}
        </Container>
    );
};

export default DividendCalculator;